import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useTenantModuleEnabled } from "@/entities/dashboard";
import { ROLES } from "@/shared/lib/rbac";
import { aiKefuService } from "../api/aiKefuService";

/**
 * 首页「客服配额已耗尽」横幅(0926 修复波第五批 P14,后端契约由代理 O 定)。
 *
 * ★为什么要有:配额用完后 AI 不再回复,客户在微信里什么也收不到,而管理员此前毫不知情。
 * ★只对 TENANT_ADMIN 显示 —— 其它角色**不请求**这个接口(接口本身全租户角色可读)。
 * ★可关:关掉后**当天**不再弹。用 localStorage 记,key 带后端给的 date(换天自然失效);
 *   存储不可用(隐私窗口等)时退化为「本次会话内关掉」,不报错。
 */
export const CS_QUOTA_DISMISS_KEY_PREFIX = "gscq.csQuotaBanner.dismissed.";

const FALLBACK_MESSAGE = "今天的 AI 客服次数已用完,客户发来的消息暂时不会被 AI 回复。";

function readDismissed(date: string): boolean {
  try {
    return window.localStorage.getItem(CS_QUOTA_DISMISS_KEY_PREFIX + date) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(date: string) {
  try {
    window.localStorage.setItem(CS_QUOTA_DISMISS_KEY_PREFIX + date, "1");
  } catch {
    /* 存储不可用:只在本次会话内关掉 */
  }
}

export function CsQuotaExhaustedBanner() {
  const { effectiveRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = effectiveRole === ROLES.TENANT_ADMIN;
  // B1 契约 ③(0927):客服舱 module 门上线后,未开通客服舱的租户请求 quota-status 会 403 MODULE_NOT_ENABLED。
  //   先看 /me/modules 含 ai_kefu 再请求;没拿到(undefined)/ 不含 → 静默不请求,也不弹错误 toast。
  const kefuEnabled = useTenantModuleEnabled("ai_kefu");
  const { data } = useQuery({
    queryKey: ["customer-service", "quota-status"],
    queryFn: () => aiKefuService.getQuotaStatus(),
    enabled: isAdmin && kefuEnabled === true,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  const [closedDate, setClosedDate] = useState<string | null>(null);

  if (!isAdmin || !data?.exhausted) return null;
  const date = data.date;
  if (closedDate === date || readDismissed(date)) return null;

  const skipped = Number.isFinite(data.skippedMessages) ? data.skippedMessages : 0;

  return (
    <div
      role="alert"
      data-testid="cs-quota-exhausted-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2.5 text-sm text-foreground"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1">
        {data.message || FALLBACK_MESSAGE}
        {skipped > 0 && (
          <span className="text-muted-foreground">
            {" "}
            今天已有 <b className="text-foreground">{skipped.toLocaleString("zh-CN")}</b>{" "}
            条客户消息没有被 AI 回复。
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => navigate("/my-billing")}
        className="whitespace-nowrap text-xs font-medium text-primary hover:underline"
      >
        查看套餐
      </button>
      <button
        type="button"
        aria-label="今天不再提示"
        title="今天不再提示"
        onClick={() => {
          writeDismissed(date);
          setClosedDate(date);
        }}
        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
